import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import InsightsIcon from '@mui/icons-material/Insights';
import { apiService } from '../services/apiService';

const SuperAdminAdvancedMonitoringPage: React.FC = () => {
  const [days, setDays] = useState('14');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<any>({});
  const [alerts, setAlerts] = useState<any[]>([]);
  const [helpTelemetry, setHelpTelemetry] = useState<{
    totalQueries: number;
    fallbackRate: number;
    moduleMismatchRate: number;
    satisfactionScore: number;
    topFailedQueries: Array<{ reason: string; count: number }>;
  } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [summaryRes, alertsRes, helpTelemetryRes] = await Promise.all([
        apiService.getAdminAdvancedMonitoringSummary({ days: Number(days) || 14 }),
        apiService.getAdminAdvancedMonitoringAlerts(),
        apiService.getHelpTelemetry(),
      ]);
      setSummary(summaryRes || {});
      setAlerts(alertsRes || []);
      setHelpTelemetry(helpTelemetryRes || null);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load advanced monitoring');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <InsightsIcon />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Advanced Monitoring
        </Typography>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            size="small"
            select
            label="Window"
            value={days}
            onChange={(e) => setDays(e.target.value)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="7">Last 7 days</MenuItem>
            <MenuItem value="14">Last 14 days</MenuItem>
            <MenuItem value="30">Last 30 days</MenuItem>
          </TextField>
          <Button variant="contained" onClick={load} disabled={loading}>
            Refresh
          </Button>
        </Box>
      </Paper>

      {loading && <CircularProgress size={22} />}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Help Assistant Telemetry
        </Typography>
        {!helpTelemetry ? (
          <Typography variant="body2" color="text.secondary">
            Telemetry not available.
          </Typography>
        ) : (
          <>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 1.5, mb: 1.5 }}>
              <Paper variant="outlined" sx={{ p: 1.25 }}>
                <Typography variant="caption" color="text.secondary">Total Queries</Typography>
                <Typography variant="h6">{helpTelemetry.totalQueries}</Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 1.25 }}>
                <Typography variant="caption" color="text.secondary">Fallback Rate</Typography>
                <Typography variant="h6">{(helpTelemetry.fallbackRate * 100).toFixed(1)}%</Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 1.25 }}>
                <Typography variant="caption" color="text.secondary">Module Mismatch Rate</Typography>
                <Typography variant="h6">{(helpTelemetry.moduleMismatchRate * 100).toFixed(1)}%</Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 1.25 }}>
                <Typography variant="caption" color="text.secondary">Satisfaction Score</Typography>
                <Typography variant="h6">{(helpTelemetry.satisfactionScore * 100).toFixed(1)}%</Typography>
              </Paper>
            </Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Top Failed Query Reasons</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Reason</TableCell>
                  <TableCell align="right">Count</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(helpTelemetry.topFailedQueries || []).map((r, i) => (
                  <TableRow key={`hf-${i}`}>
                    <TableCell>{r.reason}</TableCell>
                    <TableCell align="right">{r.count}</TableCell>
                  </TableRow>
                ))}
                {!helpTelemetry.topFailedQueries?.length && (
                  <TableRow>
                    <TableCell colSpan={2}>
                      <Typography variant="body2" color="text.secondary">No failed-query reasons recorded yet.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </>
        )}
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Alerts
        </Typography>
        {!alerts.length ? (
          <Typography variant="body2" color="text.secondary">No active alerts.</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {alerts.map((a, idx) => (
              <Box key={`${a.type}-${idx}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  size="small"
                  label={a.type}
                  color={a.severity === 'HIGH' ? 'error' : 'warning'}
                />
                <Typography variant="body2">{JSON.stringify(a.details)}</Typography>
              </Box>
            ))}
          </Box>
        )}
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Proxy Downloads Per User
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell align="right">Downloads</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(summary.proxyDownloadsPerUser || []).map((r: any, i: number) => (
              <TableRow key={`p-${i}`}>
                <TableCell>{r.userEmail || `#${r.userId}`}</TableCell>
                <TableCell align="right">{r.downloadCount}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Tool Usage Frequency
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Action</TableCell>
              <TableCell align="right">Count</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(summary.toolUsageFrequency || []).map((r: any, i: number) => (
              <TableRow key={`t-${i}`}>
                <TableCell>{r.actiontype || r.actionType}</TableCell>
                <TableCell align="right">{r.usagecount || r.usageCount}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          System Access Time (Hour of Day)
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Hour</TableCell>
              <TableCell align="right">Access Count</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(summary.systemAccessByHour || []).map((r: any, i: number) => (
              <TableRow key={`h-${i}`}>
                <TableCell>{Math.trunc(Number(r.hourofday ?? r.hourOfDay))}:00</TableCell>
                <TableCell align="right">{r.accesscount || r.accessCount}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default SuperAdminAdvancedMonitoringPage;

